import { type IExpenseRepository } from '@webapp/modules/expense/business-logic/interfaces/IExpenseRepository'
import { type ISettingsRepository } from '@webapp/platform/finance'
import { ExpenseUseCaseErrors } from '../ExpenseUseCaseErrors'

interface ApproveExpenseParams {
  expenseId: number
  nextApproverId: Nullable<number>
  shouldSkipBudgetCheck?: boolean
}

interface Dependency {
  expenseRepository: IExpenseRepository
  settingsRepository: ISettingsRepository
}

export class ApproveExpenseUseCase {
  constructor(private readonly deps: Dependency) {}

  execute = async ({
    expenseId,
    nextApproverId,
    shouldSkipBudgetCheck = false,
  }: ApproveExpenseParams): Promise<void> => {
    if (!shouldSkipBudgetCheck) {
      const budgetResult = await this.deps.expenseRepository.checkBudget(
        expenseId,
        nextApproverId
      )

      if (budgetResult === 'not_within_budget') {
        // Fail-safe: treat unreachable finance settings as overage-disabled so
        // a network blip doesn't silently grant overage confirmation. Matches
        // the SubmitExpenseReportUseCase convention.
        let isBudgetOverageEnabled: boolean
        try {
          const { data } =
            await this.deps.settingsRepository.getFinanceSettings()
          isBudgetOverageEnabled = data.isBudgetOverageEnabled
        } catch {
          isBudgetOverageEnabled = false
        }

        if (isBudgetOverageEnabled) {
          throw new ExpenseUseCaseErrors({
            name: 'EXPENSE_APPROVE_OVER_BUDGET_CONFIRMATION_REQUIRED',
            message:
              'Expense is over budget but overage is allowed with confirmation',
          })
        } else {
          throw new ExpenseUseCaseErrors({
            name: 'EXPENSE_APPROVE_OVER_BUDGET',
            message: 'Expense is over budget',
          })
        }
      }
    }

    await this.deps.expenseRepository.approve(expenseId, nextApproverId)
  }
}
